/**
 * Imperative `can()` — the single entry point for policy evaluation
 * (design §14). Components use the `useCan` hook (which reads the same cache and
 * re-renders when a verdict settles); `can()` is also the force-refresh path
 * after a mutation was rejected with 403 (§14.5: the server wins).
 */

import { getUser } from '@/lib/auth';
import { store } from '@/store/store';

import {
  beginInflight,
  invalidatePermissionResource,
  permissionKey,
  readPermission,
  resetPermissionCache,
  writePermission,
  writePermissionFromResult,
} from './permissionCache';
import { policyApi } from './policyApi';
import type { PermissionEntry } from './permissionCache';

/**
 * The evaluation subject: `userId` is the decoded access-token claim (`sub`)
 * held by the auth slice — never a form field. `null` means no session.
 */
export function currentUserId(): string | null {
  const fromStore = store.getState().auth.user?._id;
  return fromStore ?? getUser()?._id ?? null;
}

async function evaluate(
  key: string,
  action: string,
  resource: string,
): Promise<PermissionEntry | undefined> {
  const userId = currentUserId();
  if (!userId) {
    // Nothing to evaluate against. Fail closed, but only briefly so the control
    // reappears as soon as the session is back.
    writePermission(key, false, { ttlMs: 5_000, failed: true });
    return readPermission(key);
  }

  // Awaits the *result object* (never `.unwrap()`), so a denied/failed evaluation
  // is data for this module and produces no unhandled rejection. RTK Query evicts
  // the mutation entry on its own schedule; the verdict itself lives in
  // `permissionCache`, which is what the components read.
  const result = await store.dispatch(
    policyApi.endpoints.evaluatePolicy.initiate({ userId, action, resource }),
  );

  if ('data' in result && result.data) {
    return writePermissionFromResult(key, result.data);
  }

  // A 403 from the evaluation endpoint itself, a network failure or an empty
  // body all resolve to "not allowed" for the UI (fail closed).
  writePermission(key, false, { ttlMs: 10_000, failed: true });
  return readPermission(key);
}

/**
 * Resolve `action` on `resource`, reusing a fresh verdict and de-duplicating
 * concurrent checks for the same pair. Resolves `false` on any failure.
 */
export function can(action: string, resource: string): Promise<boolean> {
  const key = permissionKey(action, resource);
  const fresh = readPermission(key);
  if (fresh) {
    return Promise.resolve(fresh.allowed);
  }
  return beginInflight(key, () => evaluate(key, action, resource)).then((entry) =>
    entry ? entry.allowed : false,
  );
}

/** Cached verdict without triggering an evaluation (`undefined` = unresolved). */
export function peekPermission(action: string, resource: string): PermissionEntry | undefined {
  return readPermission(permissionKey(action, resource));
}

/**
 * Wipe every verdict for a resource (all actions). Called from the RTK Query
 * `onQueryStarted` handlers after member/role/project mutations (§14.4/§14.5):
 * the entry disappears, so `useCan` re-evaluates on the next render and a
 * previously-denied control can come back when the server says it should.
 */
export function invalidateResource(resource: string): void {
  invalidatePermissionResource(resource);
}

/**
 * Reset the cache whenever the session user changes (login, logout, forced
 * expiry). Subscribed here so the permissions feature owns its own lifecycle
 * instead of editing the shell's auth slice.
 */
let sessionWatcherInstalled = false;

export function installPermissionSessionWatcher(): void {
  if (sessionWatcherInstalled) {
    return;
  }
  sessionWatcherInstalled = true;

  let lastUserId = currentUserId();
  store.subscribe(() => {
    const nextUserId = currentUserId();
    if (nextUserId !== lastUserId) {
      lastUserId = nextUserId;
      resetPermissionCache();
    }
  });
}

installPermissionSessionWatcher();
